import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  useGetOrderSummaryMutation,
  usePurchaseProxyMutation,
} from '../../../store/api/proxiesApi';
import {
  PurchaseProxyRequestServer3,
  ProxyOrderSummaryData,
  Server3Country,
  Server3Options,
  Server3NextOptions,
  Server3State,
} from '../../../types/api';
import { ButtonSpinner, Loader } from '../../ui/Loader';

const SERVER_ID = 3;

function getPurchaseTypeLabel(id: string, t: (key: string) => string): string {
  if (id === 'by_state') return t('proxies.wizard.server3.buyByState');
  if (id === 'other_countries') return t('proxies.wizard.server3.otherCountries');
  return id;
}

export function Server3Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<'purchaseType' | 'state' | 'country' | 'config' | 'review'>('purchaseType');
  const [selectedPurchaseType, setSelectedPurchaseType] = useState<string>('');
  const [stateSearch, setStateSearch] = useState('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [selectedCarrier, setSelectedCarrier] = useState<string>('');
  const [proxyType, setProxyType] = useState<string>('HTTPS');
  const [days, setDays] = useState<number>(30);
  const proxyCount = 1;
  const [rotation, setRotation] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProxyOrderSummaryData | null>(null);

  const { data: optionsData, isLoading: loadingOptions } = useGetServerOptionsQuery(SERVER_ID);
  const [purchaseProxy, { isLoading: isPurchasing }] = usePurchaseProxyMutation();
  const [getOrderSummary, { isLoading: loadingSummary }] = useGetOrderSummaryMutation();

  const { data: nextOptionsData, isLoading: loadingNextOptions } =
    useGetServerNextOptionsQuery(
      {
        serverId: SERVER_ID,
        params: { purchaseType: selectedPurchaseType },
      },
      { skip: !selectedPurchaseType || step === 'purchaseType' }
    );

  const options = optionsData as Server3Options | undefined;
  const nextOptionsRaw = nextOptionsData as Server3NextOptions | undefined;
  const optionsPayload = (options as { data?: Server3Options })?.data ?? options;
  const nextOptionsPayload = (nextOptionsRaw as { data?: Server3NextOptions })?.data ?? nextOptionsRaw;

  const allowedDays = optionsPayload?.options?.allowedDays ?? [1, 3, 7, 15, 30];
  const rotationPeriods = optionsPayload?.options?.rotationPeriods ?? [5, 10, 30];

  const allStates: Server3State[] = nextOptionsPayload?.nextOptions?.states ?? [];
  const states: Server3State[] = stateSearch.trim()
    ? allStates.filter(
        (s) =>
          s.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
          s.code.toLowerCase().includes(stateSearch.toLowerCase())
      )
    : allStates;

  const allCountries: Server3Country[] = nextOptionsPayload?.nextOptions?.countries ?? [];
  const countries: Server3Country[] = countrySearch.trim()
    ? allCountries.filter(
        (c) =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : allCountries;

  const selectedStateData = allStates.find((s) => s.code === selectedState);
  const carriersForState = selectedStateData?.carriers?.filter((c) => (c.total_proxies ?? 0) > 0) ?? [];

  // Fetch order summary when on review step (Server 3)
  useEffect(() => {
    if (step !== 'review') return;
    if (selectedPurchaseType === 'by_state' && !selectedState) return;
    if (selectedPurchaseType === 'other_countries' && !selectedCountryCode) return;
    setError(null);
    const body = {
      proxyCount: 1,
      days,
      proxyType,
      ...(selectedPurchaseType === 'by_state'
        ? { regionCode: selectedState }
        : { countryCode: selectedCountryCode }),
      ...(selectedCarrier ? { carrier: selectedCarrier } : {}),
      ...(rotation !== '' ? { rotation } : {}),
    };
    getOrderSummary({ serverId: 3, body })
      .unwrap()
      .then((res) => setSummary(res.data))
      .catch((err: unknown) => {
        const message =
          (err as { data?: { message?: string }; message?: string })?.data?.message ||
          (err as { message?: string })?.message ||
          t('proxies.purchase.error');
        setError(message);
        setSummary(null);
      });
  }, [step, selectedPurchaseType, selectedState, selectedCountryCode, days, proxyType, selectedCarrier, rotation]);

  const handlePurchaseTypeSelect = (purchaseType: string) => {
    setSelectedPurchaseType(purchaseType);
    setError(null);
    if (purchaseType === 'by_state') {
      setStep('state');
      setSelectedState('');
      setSelectedCarrier('');
    } else {
      setStep('country');
      setSelectedCountryCode('');
    }
  };

  const handleStateSelect = (stateCode: string) => {
    setSelectedState(stateCode);
    setSelectedCarrier('');
    setStep('config');
    setError(null);
  };

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    setStep('config');
    setError(null);
  };

  const handleBack = () => {
    if (step === 'state' || step === 'country') {
      setStep('purchaseType');
      setSelectedPurchaseType('');
      setSelectedState('');
      setSelectedCountryCode('');
      setStateSearch('');
      setCountrySearch('');
      setSelectedCarrier('');
    } else if (step === 'config') {
      if (selectedPurchaseType === 'by_state') {
        setStep('state');
      } else {
        setStep('country');
      }
    } else if (step === 'review') {
      setStep('config');
      setSummary(null);
    }
  };

  const handleNext = () => {
    setError(null);
    if (step === 'config') {
      if (!proxyType || !days || !proxyCount) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      if (selectedPurchaseType === 'by_state' && !selectedState) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      if (selectedPurchaseType === 'other_countries' && !selectedCountryCode) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      setStep('review');
    }
  };

  const handlePurchase = async () => {
    setError(null);
    try {
      const body: PurchaseProxyRequestServer3 = {
        serverId: 3,
        proxyCount,
        days,
        proxyType,
        ...(selectedPurchaseType === 'by_state'
          ? { regionCode: selectedState }
          : { countryCode: selectedCountryCode }),
        ...(selectedCarrier ? { carrier: selectedCarrier } : {}),
        ...(rotation !== '' ? { rotation } : {}),
      };

      const response = await purchaseProxy(body).unwrap();

      toast.success(t('proxies.purchase.success'));
      if (response.data.proxies && response.data.proxies.length > 0 && response.data.proxies[0]) {
        navigate(`/proxies/${response.data.proxies[0]._id}`);
      } else {
        navigate('/proxies/list');
      }
    } catch (err: unknown) {
      const message =
        (err as { data?: { message?: string }; message?: string })?.data?.message ||
        (err as { message?: string })?.message ||
        t('proxies.purchase.error');
      setError(message);
      toast.error(message);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  const purchaseTypes = optionsPayload?.options?.purchaseTypes ?? [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div
            className={`flex items-center ${step === 'purchaseType' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'purchaseType'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              1
            </div>
          </div>
          <div className="flex-1 h-0.5 bg-neutral-dark mx-4" />
          {(selectedPurchaseType === 'by_state' || selectedPurchaseType === 'other_countries') && (
            <>
              <div
                className={`flex items-center ${step === 'state' || step === 'country' ? 'text-brand-accent' : 'text-neutral-medium'}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    step === 'state' || step === 'country'
                      ? 'border-brand-accent bg-brand-accent/20'
                      : 'border-neutral-medium'
                  }`}
                >
                  2
                </div>
              </div>
              <div className="flex-1 h-0.5 bg-neutral-dark mx-4" />
            </>
          )}
          <div
            className={`flex items-center ${step === 'config' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'config'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              {selectedPurchaseType ? '3' : '2'}
            </div>
          </div>
          <div className="flex-1 h-0.5 bg-neutral-dark mx-4" />
          <div
            className={`flex items-center ${step === 'review' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'review'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              {selectedPurchaseType ? '4' : '3'}
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Select Purchase Type */}
      {step === 'purchaseType' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server3.selectType')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {purchaseTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => handlePurchaseTypeSelect(type.id)}
                className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-left"
              >
                <h3 className="font-heading font-semibold text-neutral-light">
                  {getPurchaseTypeLabel(type.id, t)}
                </h3>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2a: Select State (by_state) - list with search inside */}
      {step === 'state' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server3.selectState')}
          </h2>
          {loadingNextOptions ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="border border-neutral-dark rounded-lg bg-neutral-dark overflow-hidden">
                <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                  <input
                    type="text"
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    placeholder={t('proxies.wizard.server3.searchState')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body placeholder-neutral-medium"
                  />
                </div>
                <ul className="max-h-80 overflow-y-auto py-1 bg-neutral-dark">
                  {states.map((state) => (
                    <li key={state.code}>
                      <button
                        type="button"
                        onClick={() => handleStateSelect(state.code)}
                        className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                      >
                        <span className="font-medium">{state.name}</span>
                        <span className="text-neutral-medium text-sm">{state.code}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={handleBack}
                className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2b: Select Country (other_countries) - list with search inside */}
      {step === 'country' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server3.selectCountry')}
          </h2>
          {loadingNextOptions ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="border border-neutral-dark rounded-lg bg-neutral-dark overflow-hidden">
                <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder={t('proxies.wizard.server3.searchCountry')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body placeholder-neutral-medium"
                  />
                </div>
                <ul className="max-h-80 overflow-y-auto py-1 bg-neutral-dark">
                  {countries.map((country) => (
                    <li key={country.code}>
                      <button
                        type="button"
                        onClick={() => handleCountrySelect(country.code)}
                        className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                      >
                        <span className="font-medium">{country.name}</span>
                        <span className="text-neutral-medium text-sm">{country.code}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={handleBack}
                className="w-full px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Configure */}
      {step === 'config' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server3.configure')}
          </h2>
          <div className="space-y-6">
            {/* Carrier (by_state only, only carriers with total_proxies > 0, no number in label) */}
            {selectedPurchaseType === 'by_state' && carriersForState.length > 0 && (
              <div>
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server3.carrier')} ({t('proxies.wizard.optional')})
                </label>
                <select
                  value={selectedCarrier}
                  onChange={(e) => setSelectedCarrier(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                >
                  <option value="">{t('proxies.wizard.selectOption')}</option>
                  {carriersForState.map((item) => (
                    <option key={item.carrier} value={item.carrier}>
                      {item.carrier}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Proxy Type */}
            <div>
              <label className="block text-neutral-light font-body mb-2">
                {t('proxies.wizard.server3.proxyType')}
              </label>
              <select
                value={proxyType}
                onChange={(e) => setProxyType(e.target.value)}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
              >
                <option value="HTTPS">HTTPS</option>
                <option value="SOCKS5">SOCKS5</option>
              </select>
            </div>

            {/* Days - only allowedDays */}
            <div>
              <label className="block text-neutral-light font-body mb-2">
                {t('proxies.wizard.server3.days')}
              </label>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
              >
                {allowedDays.map((d) => (
                  <option key={d} value={d}>
                    {d} {t('proxies.wizard.days')}
                  </option>
                ))}
              </select>
            </div>

            {/* Rotation (optional) */}
            {rotationPeriods.length > 0 && (
              <div>
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server3.rotation')} ({t('proxies.wizard.optional')})
                </label>
                <select
                  value={rotation === '' ? '' : rotation}
                  onChange={(e) => setRotation(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                >
                  <option value="">{t('proxies.wizard.selectOption')}</option>
                  {rotationPeriods.map((m) => (
                    <option key={m} value={m}>
                      {m} min
                    </option>
                  ))}
                </select>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body">
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleBack}
                className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 'review' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.review')}
          </h2>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.purchaseType')}:</span>
              <span className="text-neutral-light font-body">
                {getPurchaseTypeLabel(selectedPurchaseType, t)}
              </span>
            </div>
            {selectedPurchaseType === 'by_state' && selectedState && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.state')}:</span>
                <span className="text-neutral-light font-body">
                  {selectedStateData?.name ?? selectedState}
                </span>
              </div>
            )}
            {selectedPurchaseType === 'other_countries' && selectedCountryCode && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.country')}:</span>
                <span className="text-neutral-light font-body">
                  {allCountries.find((c) => c.code === selectedCountryCode)?.name ?? selectedCountryCode}
                </span>
              </div>
            )}
            {selectedCarrier && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.carrier')}:</span>
                <span className="text-neutral-light font-body">{selectedCarrier}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.proxyType')}:</span>
              <span className="text-neutral-light font-body">{proxyType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.days')}:</span>
              <span className="text-neutral-light font-body">{days}</span>
            </div>
            {loadingSummary && (
              <div className="flex items-center justify-center py-2">
                <ButtonSpinner size="sm" className="text-brand-accent" />
              </div>
            )}
            {summary && !loadingSummary && (
              <div className="pt-4 mt-4 border-t border-neutral-dark space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-medium font-body">
                    {t('proxies.wizard.server1.finalPrice')}
                    {summary.isEstimate && (
                      <span className="ml-2 text-sm text-amber-400">
                        ({t('proxies.wizard.server1.priceEstimate')})
                      </span>
                    )}
                  </span>
                  <span className="text-neutral-light font-body font-semibold">
                    {(() => {
                      const finalPrice = Number(summary.finalPrice);
                      return Number.isFinite(finalPrice)
                        ? finalPrice.toFixed(2)
                        : String(summary.finalPrice);
                    })()} {summary.currency}
                  </span>
                </div>
              </div>
            )}
            {rotation !== '' && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server3.rotation')}:</span>
                <span className="text-neutral-light font-body">{rotation} min</span>
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body mb-6">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleBack}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body disabled:opacity-50"
            >
              {t('common.back')}
            </button>
            <button
              onClick={() => void handlePurchase()}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold disabled:opacity-50"
            >
              {isPurchasing ? t('common.processing') : t('proxies.purchase.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
